// <pfs-notification-settings>: generische Notification-Preferences-Matrix
// (PFS-693). Loest die je-Cut-App kopierten Prefs-WCs ab
// (mednet-user/practice-notification-preferences).
//
// SERVER-getriebener Katalog: Zeilen (Events) + Spalten (Kanaele) kommen aus
// der Read-API des Bindeglieds (mednet-notifications /me/notification-settings),
// NICHT client-hartkodiert. Zweiseitige Config: effektiv = Override ?? Policy-
// Default. Kontext-Dimension: "Allgemein" (user-weit) + je Praxis — per-Praxis
// ueberschreibt user-weit (progressive Disclosure).
//
// Read-API-Shape (huma camelCase):
//   GET  <source>  -> { events:[{eventType,category,label?,channels:[{channelKey,defaultEnabled}]}],
//                       contexts:[{scope,id,label}],
//                       overrides:[{contextScope,contextId,eventType,channelKey,enabled}] }
//   PUT  <source>  -> { changes:[{contextScope,contextId,eventType,channelKey,enabled}] } -> {applied}
//
// Attribute (alle data-*):
//   data-source-url  Read/Write-API-Basis (Default "/api/v1/me/notification-settings").
//                    Relativ (fuehrendes "/") -> apiBase() wird vorangestellt.
//   data-title       Card-Titel (Default "Benachrichtigungen").
//   data-subtitle    optionaler Untertitel.
//
// Sichtbare Strings sind fix DEUTSCH (Fleet-Konvention: deutschsprachige
// Produkte, i18n später via pfs-content/PFS-418 — kein Englisch-Default/Override
// auf Vorrat).

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { apiFetch, apiBase } from "../../store/api-fetch.js";
import { escapeHTML } from "../../utils/escape-html.js";
import "../layout/pfs-card.js";
import "../card-body/pfs-card-body.js";
import "../card-header/pfs-card-header.js";
import "../loading-indicator/pfs-loading-indicator.js";

const DEFAULT_SOURCE = "/api/v1/me/notification-settings";

// Default-Header-Icon (Glocke), slotbar ins <pfs-card-header>.
const ICON_BELL =
  '<svg slot="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/></svg>';

interface CatalogChannel {
  channelKey: string;
  defaultEnabled: boolean;
}
interface CatalogEvent {
  eventType: string;
  category: string;
  label?: string; // server-geliefertes Anzeige-Label; fehlt -> Fallback auf eventType
  channels: CatalogChannel[];
}
interface SettingsContext {
  scope: string;
  id: string;
  label: string;
}
interface Override {
  contextScope: string;
  contextId: string;
  eventType: string;
  channelKey: string;
  enabled: boolean;
}
interface SettingsBody {
  events: CatalogEvent[];
  contexts: SettingsContext[];
  overrides: Override[];
}

// human-fallback fuer channel-keys ohne server-Label (Katalog liefert nur keys).
const CHANNEL_LABELS: Record<string, string> = {
  inbox: "Postfach",
  email: "E-Mail",
  realtime: "Echtzeit",
};
const channelLabel = (key: string): string => CHANNEL_LABELS[key] ?? key;

const ovKey = (ctxScope: string, ctxId: string, event: string, channel: string): string =>
  `${ctxScope} ${ctxId} ${event} ${channel}`;

const STYLES = `
${SHADOW_RESET_CSS}
:host { display: block; container-type: inline-size; }
.ctx-row { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.ctx-select { font: inherit; padding: 0.3rem 0.5rem; border-radius: var(--pfs-radius-md, 0.5rem); border: 1px solid var(--pfs-color-outline); background: var(--pfs-color-surface); color: var(--pfs-color-on-surface); }
.table-card { border: 1px solid var(--pfs-color-outline); border-radius: var(--pfs-radius-md, 0.5rem); overflow-x: auto; }
table.prefs { width: 100%; border-collapse: collapse; }
table.prefs th, table.prefs td { padding: 0.6rem 0.75rem; text-align: left; border-bottom: 1px solid var(--pfs-color-outline); }
table.prefs tbody tr:last-child td { border-bottom: 0; }
table.prefs tbody tr:hover { background: var(--pfs-color-surface-hover); }
th.ch { text-align: center; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--pfs-color-on-surface-muted); }
th.evt { font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--pfs-color-on-surface-muted); }
td.ch { text-align: center; }
td.label { font-weight: 500; color: var(--pfs-color-on-surface); }
td.na { text-align: center; color: var(--pfs-color-on-surface-subtle); }
input[type="checkbox"] { width: 1.05rem; height: 1.05rem; accent-color: var(--pfs-color-primary); cursor: pointer; }
.save-bar { display: flex; align-items: center; gap: 0.75rem; justify-content: flex-start; }
.save { font: inherit; font-weight: 600; padding: 0.45rem 0.9rem; border: 0; border-radius: var(--pfs-radius-md, 0.5rem); background: var(--pfs-color-primary); color: var(--pfs-color-on-primary); cursor: pointer; }
.save:disabled { opacity: 0.5; cursor: default; }
.error { font-size: 0.875rem; color: var(--pfs-color-danger); }
.error:empty { display: none; }
`;

defineComponent({
  tagName: "pfs-notification-settings",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;

    const rawSource = host.getAttribute("data-source-url")?.trim() || DEFAULT_SOURCE;
    const sourceUrl = rawSource.startsWith("/") ? apiBase() + rawSource : rawSource;
    const title = host.getAttribute("data-title")?.trim() || "Benachrichtigungen";
    const subtitle = host.getAttribute("data-subtitle")?.trim() || "";

    let data: SettingsBody | null = null;
    let currentCtx: SettingsContext = { scope: "", id: "", label: "" };
    // dirty: ovKey(currentCtx, event, channel) -> enabled (explizit gesetzt)
    const dirty = new Map<string, { ctxScope: string; ctxId: string; eventType: string; channelKey: string; enabled: boolean }>();
    // overrides-Lookup ueber alle Kontexte (aus dem letzten load).
    let overrideMap = new Map<string, boolean>();

    const style = document.createElement("style");
    style.textContent = STYLES;
    // mount hält die sektionierte Card (paint), den Loading- bzw. Fehler-Zustand.
    const mount = document.createElement("div");
    root.append(style, mount);

    // Geteilter Header (Titel/Untertitel/Glocke) — identisch in Loading + paint,
    // damit der Header beim Übergang nicht springt (F1).
    const buildHeader = (): HTMLElement => {
      const header = document.createElement("pfs-card-header");
      header.setAttribute("data-title", title);
      if (subtitle) header.setAttribute("data-subtitle", subtitle);
      header.insertAdjacentHTML("afterbegin", ICON_BELL);
      return header;
    };

    // Sofortiger Loading-Skeleton — konsistent zu den Geschwister-Cards (mfa/
    // profile zeigen ihren Spinner sofort). Ohne ihn bliebe die Card leer bis der
    // Fetch fertig ist → wirkt wie „lädt erst nach der Nachbar-Card".
    const showLoading = (): void => {
      const card = document.createElement("pfs-card");
      card.setAttribute("data-sectioned", "");
      card.appendChild(buildHeader());
      const body = document.createElement("pfs-card-body");
      const li = document.createElement("pfs-loading-indicator");
      li.setAttribute("data-variant", "main");
      li.setAttribute("data-text", "Benachrichtigungen werden geladen");
      body.appendChild(li);
      card.appendChild(body);
      mount.replaceChildren(card);
    };
    showLoading();

    const buildOverrideMap = (): void => {
      overrideMap = new Map();
      for (const o of data?.overrides ?? []) {
        overrideMap.set(ovKey(o.contextScope, o.contextId, o.eventType, o.channelKey), o.enabled);
      }
    };

    // effektiv fuer den AKTUELLEN Kontext (dirty > Kontext-Override > user-weit > Default).
    const effective = (eventType: string, ch: CatalogChannel): boolean => {
      const dk = ovKey(currentCtx.scope, currentCtx.id, eventType, ch.channelKey);
      const d = dirty.get(dk);
      if (d) return d.enabled;
      if (currentCtx.scope !== "") {
        const pv = overrideMap.get(ovKey(currentCtx.scope, currentCtx.id, eventType, ch.channelKey));
        if (pv !== undefined) return pv;
      }
      const uv = overrideMap.get(ovKey("", "", eventType, ch.channelKey));
      if (uv !== undefined) return uv;
      return ch.defaultEnabled;
    };

    const paint = (): void => {
      if (!data) return;

      // Sektionierte Card-SSOT (PFS-761/PFS-760): data-sectioned → Header-Band +
      // Divider + Body-Zone. Die Card erkennt den <pfs-card-header> automatisch.
      const card = document.createElement("pfs-card");
      card.setAttribute("data-sectioned", "");
      card.appendChild(buildHeader());

      // Body-Zone: Fehler/Kontext-Switcher/Matrix/Save-Bar wohnen sektioniert.
      const body = document.createElement("pfs-card-body");

      const err = document.createElement("p");
      err.className = "error";
      body.appendChild(err);

      // Save-Button frueh erzeugen (die Checkbox-Toggles aktualisieren seinen
      // disabled-State); der save-bar-Zusammenbau folgt unten.
      const saveBtn = document.createElement("button");
      saveBtn.className = "save";
      saveBtn.type = "button";
      saveBtn.textContent = "Speichern";
      saveBtn.disabled = dirty.size === 0;

      // Kontext-Switcher (nur wenn mehr als "Allgemein" — d.h. der User ist in
      // mind. 1 Praxis). PFS-735: kein sichtbares Label mehr (das Dropdown steht
      // allein, erste Option "Allgemein" ist selbsterklärend); a11y via aria-label.
      // Der per-Praxis-Kontext ist end-to-end funktionsfähig (Settings-API +
      // Delivery-Resolution policy.go/intersect.go: per-Praxis > user-weit > Default).
      if (data.contexts.length > 1) {
        const ctxRow = document.createElement("div");
        ctxRow.className = "ctx-row";
        const sel = document.createElement("select");
        sel.className = "ctx-select";
        sel.id = "pfs-ns-ctx";
        sel.setAttribute("aria-label", "Kontext");
        for (const c of data.contexts) {
          const opt = document.createElement("option");
          opt.value = `${c.scope} ${c.id}`;
          opt.textContent = c.label;
          if (c.scope === currentCtx.scope && c.id === currentCtx.id) opt.selected = true;
          sel.appendChild(opt);
        }
        const onCtx = (): void => {
          const [scope, id] = sel.value.split(" ");
          currentCtx = data!.contexts.find((c) => c.scope === scope && c.id === id) ?? currentCtx;
          paint();
        };
        sel.addEventListener("change", onCtx);
        ctx.cleanup.push(() => sel.removeEventListener("change", onCtx));
        ctxRow.append(sel);
        body.appendChild(ctxRow);
      }

      // Spalten = Union aller Kanaele ueber alle Events (stabile Reihenfolge).
      const columns: string[] = [];
      for (const ev of data.events) {
        for (const ch of ev.channels) if (!columns.includes(ch.channelKey)) columns.push(ch.channelKey);
      }

      const tableCard = document.createElement("div");
      tableCard.className = "table-card";
      const table = document.createElement("table");
      table.className = "prefs";
      const thead = document.createElement("thead");
      let headRow = `<tr><th class="evt">Ereignis</th>`;
      for (const c of columns) headRow += `<th class="ch">${escapeHTML(channelLabel(c))}</th>`;
      headRow += `</tr>`;
      thead.innerHTML = headRow;
      const tbody = document.createElement("tbody");
      for (const ev of data.events) {
        const tr = document.createElement("tr");
        const tdLabel = document.createElement("td");
        tdLabel.className = "label";
        tdLabel.textContent = ev.label || ev.eventType;
        tr.appendChild(tdLabel);
        for (const col of columns) {
          const ch = ev.channels.find((c) => c.channelKey === col);
          const td = document.createElement("td");
          if (!ch) {
            td.className = "na";
            td.textContent = "—";
          } else {
            td.className = "ch";
            const cb = document.createElement("input");
            cb.type = "checkbox";
            cb.checked = effective(ev.eventType, ch);
            cb.setAttribute("aria-label", `${ev.label || ev.eventType} · ${channelLabel(col)}`);
            const onToggle = (): void => {
              dirty.set(ovKey(currentCtx.scope, currentCtx.id, ev.eventType, ch.channelKey), {
                ctxScope: currentCtx.scope,
                ctxId: currentCtx.id,
                eventType: ev.eventType,
                channelKey: ch.channelKey,
                enabled: cb.checked,
              });
              saveBtn.disabled = dirty.size === 0;
            };
            cb.addEventListener("change", onToggle);
            ctx.cleanup.push(() => cb.removeEventListener("change", onToggle));
            td.appendChild(cb);
          }
          tr.appendChild(td);
        }
        tbody.appendChild(tr);
      }
      table.append(thead, tbody);
      tableCard.appendChild(table);
      body.appendChild(tableCard);

      const saveBar = document.createElement("div");
      saveBar.className = "save-bar";
      const onSave = (): void => void save(err, saveBtn);
      saveBtn.addEventListener("click", onSave);
      ctx.cleanup.push(() => saveBtn.removeEventListener("click", onSave));
      saveBar.append(saveBtn);
      body.appendChild(saveBar);

      card.appendChild(body);
      mount.replaceChildren(card);
    };

    const load = async (): Promise<void> => {
      try {
        data = await apiFetch<SettingsBody>(sourceUrl);
        buildOverrideMap();
        if (data.contexts.length && !data.contexts.some((c) => c.scope === currentCtx.scope && c.id === currentCtx.id)) {
          currentCtx = data.contexts[0];
        }
        dirty.clear();
        paint();
      } catch {
        const e = document.createElement("p");
        e.className = "error";
        e.textContent = "Benachrichtigungs-Einstellungen konnten nicht geladen werden.";
        mount.replaceChildren(e);
      }
    };

    const save = async (errEl: HTMLElement, saveBtn: HTMLButtonElement): Promise<void> => {
      if (dirty.size === 0) return;
      const changes = Array.from(dirty.values()).map((d) => ({
        contextScope: d.ctxScope,
        contextId: d.ctxId,
        eventType: d.eventType,
        channelKey: d.channelKey,
        enabled: d.enabled,
      }));
      saveBtn.disabled = true;
      try {
        errEl.textContent = "";
        await apiFetch(sourceUrl, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ changes }),
        });
        await load();
      } catch {
        errEl.textContent = "Konnte nicht gespeichert werden. Bitte erneut versuchen.";
        saveBtn.disabled = false;
      }
    };

    void load();
  },
});
